import * as XLSX from 'xlsx/xlsx.mjs'

/**
 * Export packages to Excel format
 * @param {Object} params - Export parameters
 * @param {Object} params.packingList - The packing list with packages array
 * @param {Array} params.headers - Headers array from the table
 * @param {Boolean} params.onlySuggested - Whether using suggested quantities
 * @param {Object} params.settingFinish - Finish and UC code settings
 * @param {Object} params.woData - Work order data for reprocess info
 */
export function exportPackagesToExcel({
    packingList,
    headers,
    onlySuggested,
    settingFinish,
    woData,
}) {
    try {
        const workbook = XLSX.utils.book_new()
        const worksheetData = []

        // Iterate through each package
        packingList.packages.forEach((packageElement, packageIndex) => {
            // Add package header row
            const packageTitle = `Package ${packageIndex + 1}`
            const dimensions = packageElement.packageDimensions
            const dimensionsText = dimensions
                ? `(W${dimensions.width} x H${dimensions.height} x L${
                      dimensions.depth
                  } ${dimensions.units}) - Crate Mass: ${Number(
                      dimensions.mass || 0
                  ).toFixed(1)} Kg`
                : ''
            const bomText = packageElement.packageBOM
                ? ` - BOM:${packageElement.packageBOM}`
                : ''

            // Package title row
            worksheetData.push([]) // Empty row for spacing
            const titleRow = [packageTitle, dimensionsText + bomText]
            // Add empty cells for the rest of the columns
            for (let i = 2; i < headers.length; i++) {
                titleRow.push('')
            }
            worksheetData.push(titleRow)

            // Add column headers
            const headerRow = headers.map(header => header.text)
            worksheetData.push(headerRow)

            // Calculate totals for this package
            let totalSuggested = 0
            let totalPacking = 0
            let totalMass = 0

            packageElement.items.forEach(item => {
                if (onlySuggested) {
                    totalSuggested += Number(item.suggestedQty || 0)
                    if (item.mass && item.suggestedQty) {
                        totalMass +=
                            Number(item.suggestedQty) * Number(item.mass)
                    }
                } else {
                    totalPacking += Number(item.packingQty || 0)
                    if (item.mass && item.packingQty) {
                        totalMass += Number(item.packingQty) * Number(item.mass)
                    }
                }
            })

            const crateMass = Number(
                packageElement.packageDimensions?.mass || 0
            )
            const grossMass = totalMass + crateMass

            // Build items rows
            for (const item of packageElement.items) {
                const row = []
                for (const header of headers) {
                    let value = 'N/A'

                    switch (header.value) {
                        case 'code': {
                            const code = item.workOrder || item.bom
                            if (code) {
                                const reprocessText =
                                    woData && woData[item.workOrder]?.reprocess
                                        ? ' (Reprocess)'
                                        : ''
                                value = `${code}${reprocessText}`
                            }
                            break
                        }
                        case 'type':
                            value = item.bom != undefined ? 'BOM' : 'WorkOrder'
                            break
                        case 'partNumber':
                            value = item.partNumber || item.code || 'N/A'
                            break
                        case 'description':
                            value = item.description
                            break
                        case 'finishAndUCCode': {
                            if (item.finishAndUCCode && settingFinish) {
                                const dataFinishAndUCCode = settingFinish.finishes.find(
                                    e => e.id == item.finishAndUCCode
                                )
                                value = dataFinishAndUCCode
                                    ? dataFinishAndUCCode.description
                                    : 'N/A'
                            }
                            break
                        }
                        case 'mass':
                            value = item.mass ? `${Number(item.mass || 0).toFixed(1)} Kg` : 'N/A'
                            break
                        case 'packingQty':
                            value = item.packingQty || 'N/A'
                            break
                        case 'suggestedQty':
                            value = item.suggestedQty || 'N/A'
                            break
                        case 'packingMass':
                            if (item.mass && item.packingQty) {
                                value = `${(
                                    item.mass * item.packingQty
                                ).toFixed(1)} Kg`
                            }
                            break
                        case 'suggestedMass':
                            if (item.mass && item.suggestedQty) {
                                value = `${(
                                    item.mass * Number(item.suggestedQty)
                                ).toFixed(1)} Kg`
                            }
                            break
                        case 'notes':
                            value = item.notes
                            break
                        case 'oc':
                            value = item.oc || 'N/A'
                            break
                        case 'costPosition':
                            value = item.costPosition || 'N/A'
                            break
                        default:
                            value = item[header.value] || 'N/A'
                    }

                    row.push(value)
                }
                worksheetData.push(row)
            }

            // Add totals row
            const hasSuggestedProcess = headers.some(
                h => h.value === 'suggestedQty'
            )
            const totalsRow = []

            headers.forEach((header, index) => {
                if (index === 0) {
                    totalsRow.push('')
                } else if (
                    hasSuggestedProcess &&
                    header.value === 'suggestedQty'
                ) {
                    totalsRow.push(totalSuggested)
                } else if (
                    !hasSuggestedProcess &&
                    header.value === 'packingQty'
                ) {
                    totalsRow.push(totalPacking)
                } else if (
                    header.value === 'suggestedMass' ||
                    header.value === 'packingMass'
                ) {
                    totalsRow.push(`${totalMass.toFixed(1)} Kg`)
                } else if (header.value === 'oc') {
                    totalsRow.push('')
                    // Add Gross Mass right after OC column
                    totalsRow.push(`Gross Mass: ${grossMass.toFixed(1)} Kg`)
                } else {
                    totalsRow.push('')
                }
            })

            worksheetData.push(totalsRow)
            worksheetData.push([]) // Empty row for spacing between packages
        })

        // Create worksheet from array of arrays
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

        // Set column widths
        const columnWidths = headers.map(header => {
            if (header.value === 'description') {
                return { wch: 40 }
            } else if (header.value === 'finishAndUCCode') {
                return { wch: 30 }
            } else if (
                header.value === 'code' ||
                header.value === 'partNumber'
            ) {
                return { wch: 15 }
            } else {
                return { wch: 18 }
            }
        })
        worksheet['!cols'] = columnWidths

        // Add worksheet to workbook
        const sheetName = 'Packages'
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

        // Generate filename
        const code = packingList.code || 'PackingList'
        const filename = `${code}.xlsx`

        // Write file
        XLSX.writeFile(workbook, filename)

        return true
    } catch (error) {
        throw new Error(`Error exporting packages to Excel: ${error.message}`)
    }
}
